<template>
    <div class="articles-container">
        <div id="articles-wrapper">
            <template v-if="isLoading">
                <LoadMoreArticles v-for="index in perPage" :key="`shimmer-${index}`" />
            </template>
            <template v-else-if="articles && articles.length > 0">
                <ArticleBox v-for="(article, index) in articles"
                            :item="article"
                            :index="index"
                            :key="article.articleId"
                            :article="article" />
            </template>
        </div>
        <div class="load-more-container" v-if="showMoreButton">
            <button v-if="isLoadingMore" class="load-more-button">
                <span class="icon"><img :src="`/Assets/img/spin.svg`" /></span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import ArticleBox from '@/components/ArticleBox.vue'
import LoadMoreArticles from '@/components/LoadMoreArticles.vue'
import api from '@/api'
import ga from '@/helpers/ga'
import clicky from '@/helpers/clicky'
import settings from '@/settings'
import util from '@/helpers/util'
import { Article } from '@/classes/Articles'

const props = defineProps<{
    excludeArticles?: number
}>()

const route = useRoute()
const isLoading = ref(false)
const isLoadingMore = ref(false)
const isGiftList = ref(false)
const articles = ref<Article[]>([])
const perPage = ref(10)
const pageNumber = ref(1)
const showMoreButton = ref(false)
const autoLoadMoreEnabled = ref(false)

const finalExcludeArticle = computed(() => {
    return props.excludeArticles ?? 0
})

const init = async () => {
    isLoading.value = true
    isLoadingMore.value = false
    isGiftList.value = false
    articles.value = []

    if (route.name === 'giftideas' || route.name === 'giftlist' || route.name === 'giftlist2') {
        isGiftList.value = true
    }

    await loadData()
    isLoading.value = false
}

const autoLoadMore = () => {
    if (autoLoadMoreEnabled.value) {
        const loadMoreElem = document.querySelector('.load-more-container') as HTMLElement
        if (loadMoreElem && util.checkVisible(loadMoreElem)) {
            loadMoreData()
        }
    }
}

const loadMoreData = async () => {
    ga.event('click', { 'event_category': 'articlepages', 'event_label': pageNumber.value })
    clicky.log('Articles - Load More Data Page #' + pageNumber.value, 'click')
    autoLoadMoreEnabled.value = false
    isLoadingMore.value = true
    await loadData()
    isLoadingMore.value = false
}

const loadData = async () => {
    const response = await api.getArticles(perPage.value, pageNumber.value, finalExcludeArticle.value, isGiftList.value)
    
    if (response.data) {
        showMoreButton.value = response.data.length >= perPage.value

        // Convert plain objects to Article instances to ensure proper property mapping
        const tmp = response.data.map((article: any) => article instanceof Article ? article : new Article(article))
        //tmp.forEach(a => {
            //a.imgUrl = 'https://' + settings.siteUrl + a.imgUrl
        //})

        articles.value.push(...tmp)
        pageNumber.value++

        setTimeout(() => { autoLoadMoreEnabled.value = true }, 500)
    }
}

onMounted(() => {
    window.addEventListener('scroll', autoLoadMore)
    init()
})

onUnmounted(() => {
    window.removeEventListener('scroll', autoLoadMore)
})
</script>

<style scoped>
.articles-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
}

#articles-wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-bottom: 40px;
    width: 100%;
    box-sizing: border-box;
}

#articles-wrapper > * {
    min-width: 0;
    max-width: 100%;
}

@media (min-width: 1201px) {
    #articles-wrapper {
        grid-template-columns: repeat(2, 1fr);
        gap: 32px;
    }

    .articles-container {
        padding: 0 40px;
    }
}

@media (min-width: 1600px) {
    #articles-wrapper {
        gap: 40px;
    }
}

.load-more-container {
    display: flex;
    justify-content: center;
    padding: 40px 0;
    width: 100%;
    box-sizing: border-box;
}

.load-more-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 12px 24px;
}

.load-more-button .icon img {
    width: 32px;
    height: 32px;
}

@media (max-width: 1200px) {
    .articles-container {
        padding: 0 16px;
    }

    #articles-wrapper {
        gap: 20px;
        margin-bottom: 32px;
    }
}

@media (max-width: 640px) {
    .articles-container {
        padding: 0 12px;
    }

    #articles-wrapper {
        gap: 16px;
        margin-bottom: 24px;
    }

    .load-more-container {
        padding: 32px 0;
    }
}
</style>
